import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import {
  ArrowRight, Brain, Compass, Flame, Sprout, Users, ChevronRight, Lock, Star,
  BookOpen, MessageCircle, Sparkles, Heart, Phone, MessageSquare, Library,
  ShieldCheck, FileText, Quote, CheckCircle2, Send, Zap, Wind, RefreshCw, TreePine, Trophy,
  Play, Pause, Music, Disc3, ChevronDown,
} from 'lucide-react';
import WaitlistBell from '@/components/WaitlistBell';
import AnimatedStat from '@/components/AnimatedStat';

const PILLAR_ICONS = { Mindset: Brain, Strength: Flame, Purpose: Compass, Growth: Sprout, Community: Users };
const PILLARS = [
  { name: 'Mindset', tagline: 'Master your thoughts', desc: 'Catch the lies your mind tells you. Rewrite the story before it writes you.' },
  { name: 'Strength', tagline: 'Build mental toughness', desc: 'Train resilience like an athlete. Show up when it’s hard — that’s where you grow.' },
  { name: 'Purpose', tagline: 'Find your why', desc: 'Anchor your life in meaning. Goals fade. Purpose keeps you moving.' },
  { name: 'Growth', tagline: 'Get 1% better daily', desc: 'Small reps. Big compounding. The version of you in a year is built today.' },
  { name: 'Community', tagline: 'You’re not alone', desc: 'Honest. Supportive. No fake positivity. People walking the same road.' },
];

export default function SplashPage() {
  const [c, setC] = useState(null);
  const [headspace, setHeadspace] = useState(null);
  const [counts, setCounts] = useState({ community: 0, courses: {} });

  useEffect(() => {
    api.get('/splash').then(r => setC(r.data)).catch(() => {});
    api.get('/courses/headspace').then(r => setHeadspace(r.data)).catch(() => {});
    api.get('/waitlist/counts').then(r => setCounts(r.data || { community: 0, courses: {} })).catch(() => {});
  }, []);

  if (!c) return <div className="min-h-screen bg-white" />;
  const vis = c.sections_visible || {};
  const comingSoon = c.coming_soon_courses || [];
  const headspaceCover = headspace?.cover_image || '/splash/headspace_cover.png';

  return (
    <div className="bg-white text-ink">
      {/* ============ 1) CINEMATIC HERO ============ */}
      <section className="relative overflow-hidden bg-white" data-testid="splash-hero">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(900px 500px at 20% 20%, rgba(212,164,55,0.15), transparent 60%), radial-gradient(700px 400px at 95% 80%, rgba(212,164,55,0.10), transparent 60%)',
        }} />
        <div className="relative kc-container py-14 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 kc-fade-up">
            <div className="inline-flex items-center gap-2 bg-gold-soft border border-[color:var(--kc-gold)] text-ink rounded-full px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase font-semibold" data-testid="splash-hero-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--kc-gold)] animate-pulse" /> {c.hero_eyebrow}
            </div>
            <h1 className="mt-5 font-display uppercase leading-[0.9] tracking-[-0.005em]" style={{ fontSize: 'clamp(2.75rem, 8vw, 7rem)' }} data-testid="splash-hero-title">
              <span className="block">MASTER YOUR MIND.</span>
              <span className="block text-[color:var(--kc-gold)]">BUILD YOUR LIFE.</span>
            </h1>
            <p className="mt-5 text-lg md:text-xl text-ink-2 max-w-xl leading-relaxed" data-testid="splash-hero-subtitle">{c.hero_subtitle}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to={c.hero_cta_primary_href || '/courses/headspace'} className="group h-13 px-7 py-3 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold text-base hover:bg-[color:var(--kc-gold-2)] transition-all hover:gap-3 shadow-[0_10px_30px_-10px_rgba(212,164,55,0.5)]" data-testid="splash-hero-cta-1">
                {c.hero_cta_primary_label} <ArrowRight size={20} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link to={c.hero_cta_secondary_href || '/signup'} className="h-13 px-7 py-3 inline-flex items-center rounded-[var(--kc-radius-sm)] border border-ink/30 text-ink font-semibold hover:bg-ink hover:text-white transition-colors" data-testid="splash-hero-cta-2">{c.hero_cta_secondary_label}</Link>
            </div>
            <div className="mt-7 flex items-center gap-3 text-sm text-muted-kc">
              <div className="flex">{[1, 2, 3, 4, 5].map(i => <Star key={i} size={14} className="fill-[color:var(--kc-gold)] text-[color:var(--kc-gold)]" />)}</div>
              <span>{c.hero_trust_line || 'Trusted by readers across the country.'}</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative flex items-end justify-center gap-3 md:gap-4">
              <div className="relative w-1/3 transform rotate-[-8deg] translate-y-4 shadow-2xl rounded-md overflow-hidden border border-kc">
                <img alt="" src="/splash/manifesto_storm.png" className="w-full aspect-[3/4] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-2 text-white text-[10px] font-display uppercase tracking-wider">Coming soon</div>
              </div>
              <div className="relative w-2/5 z-10 transform shadow-2xl rounded-md overflow-hidden border border-kc">
                <img alt="HEADSPACE book" src={headspaceCover} onError={(e) => { if (e.currentTarget.src.indexOf('/splash/headspace_cover.png') === -1) e.currentTarget.src = '/splash/headspace_cover.png'; }} className="w-full aspect-[3/4] object-cover bg-ink" />
                <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[color:var(--kc-gold)] text-black text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Available now</div>
              </div>
              <div className="relative w-1/3 transform rotate-[8deg] translate-y-4 shadow-2xl rounded-md overflow-hidden border border-kc">
                <img alt="" src="/splash/cta_horizon.png" className="w-full aspect-[3/4] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-2 text-white text-[10px] font-display uppercase tracking-wider">Coming soon</div>
              </div>
            </div>
            <div className="mt-6 text-center text-xs uppercase tracking-[0.18em] text-muted-kc">A growing library of mindset-driven courses.</div>
          </div>
        </div>
      </section>

      {/* ============ Pillar band ============ */}
      <section className="relative bg-ink text-white py-14 md:py-20 overflow-hidden" data-testid="splash-pillars-band">
        <div className="absolute inset-0 pointer-events-none kc-noise opacity-50" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(700px 380px at 50% 0%, rgba(212,164,55,0.10), transparent 60%)' }} />
        <div className="relative kc-container">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">The Five Pillars</div>
            <h2 className="mt-3 font-display uppercase leading-[0.92]" style={{ fontSize: 'clamp(2rem, 5vw, 3.75rem)' }}>What we’re building.</h2>
            <p className="mt-3 text-white/70">Every course, resource, and tool here ladders up to one of these five. They’re the foundation of a stronger life.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {PILLARS.map((p, i) => {
              const Icon = PILLAR_ICONS[p.name];
              return (
                <div key={i} className="group relative bg-white/5 backdrop-blur-md border border-white/10 hover:border-[color:var(--kc-gold)] rounded-[var(--kc-radius-md)] p-5 transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(212,164,55,0.4)]">
                  <div className="font-display text-3xl text-[color:var(--kc-gold)] opacity-50 leading-none">{String(i + 1).padStart(2, '0')}</div>
                  <div className="mt-3 w-12 h-12 rounded-full bg-[color:var(--kc-gold)]/10 border border-[color:var(--kc-gold)]/30 text-[color:var(--kc-gold)] flex items-center justify-center group-hover:bg-[color:var(--kc-gold)] group-hover:text-black transition-colors">
                    <Icon size={22} />
                  </div>
                  <div className="mt-4 font-display uppercase text-xl leading-tight">{p.name}</div>
                  <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-[color:var(--kc-gold)]/80 font-semibold">{p.tagline}</div>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ 2) HEADSPACE FEATURED COURSE ============ */}
      {vis.headspace_feature !== false && headspace && (
        <section className="relative bg-white py-16 md:py-24" data-testid="splash-headspace-feature">
          <div className="kc-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative rounded-[var(--kc-radius-md)] overflow-hidden border border-kc-strong shadow-2xl bg-ink">
                <img src={headspaceCover} alt="HEADSPACE" className="w-full aspect-[3/4] object-cover" />
                <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[color:var(--kc-gold)] text-black text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">Available now</div>
              </div>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]">The flagship course</div>
              <h2 className="mt-3 font-display uppercase leading-[0.9]" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>HEADSPACE</h2>
              <div className="mt-2 font-display uppercase text-xl md:text-2xl text-ink-2">{headspace.subtitle || 'Control your thoughts. Control your life.'}</div>
              <p className="mt-5 text-lg text-ink-2 leading-relaxed max-w-xl">{headspace.tagline || headspace.description}</p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl">
                {(headspace.what_youll_learn || []).slice(0, 6).map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-ink-2"><CheckCircle2 size={16} className="text-[color:var(--kc-gold)] mt-0.5 flex-shrink-0" /> {w}</li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to="/courses/headspace" className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90 transition-colors" data-testid="headspace-start-btn">
                  Start the course <ArrowRight size={16} />
                </Link>
                <span className="text-sm text-muted-kc inline-flex items-center gap-2"><BookOpen size={14} /> {headspace.lesson_count || 18} lessons · Pay what you want</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ 3) FREE MICK AI COACH ============ */}
      {vis.mick_coach !== false && (
        <section className="relative bg-ink text-white py-16 md:py-24 overflow-hidden" data-testid="splash-mick">
          <div className="absolute inset-0 pointer-events-none kc-noise opacity-50" />
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(600px 380px at 75% 50%, rgba(212,164,55,0.18), transparent 60%)',
          }} />
          <div className="relative kc-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">
                <Sparkles size={12} /> {c.mick_eyebrow}
              </div>
              <h2 className="mt-4 font-display uppercase leading-[0.9]" style={{ fontSize: 'clamp(2.5rem, 6.5vw, 5.5rem)' }} data-testid="mick-title">
                {c.mick_title}
              </h2>
              <p className="mt-5 text-lg text-white/80 max-w-xl leading-relaxed">{c.mick_body}</p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl">
                {(c.mick_features || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/85">
                    <CheckCircle2 size={16} className="text-[color:var(--kc-gold)] mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to={c.mick_cta_href || '/coach'} className="group h-13 px-7 py-3 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold text-base hover:bg-[color:var(--kc-gold-2)] transition-all hover:gap-3 shadow-[0_10px_40px_-10px_rgba(212,164,55,0.7)]" data-testid="mick-cta">
                  <MessageCircle size={18} /> {c.mick_cta_label} <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" />
                </Link>
                <span className="text-xs uppercase tracking-[0.18em] text-white/50">Free · No credit card</span>
              </div>
            </div>

            {/* Chat bubble preview */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 rounded-[var(--kc-radius-md)] bg-[color:var(--kc-gold)]/15 blur-2xl pointer-events-none" />
                <div className="relative bg-white/5 backdrop-blur-md border border-white/10 rounded-[var(--kc-radius-md)] p-5 shadow-[0_30px_80px_-30px_rgba(212,164,55,0.4)]">
                  <div className="flex items-center gap-3 pb-4 border-b border-white/10">
                    <div className="w-10 h-10 rounded-full bg-[color:var(--kc-gold)] text-black inline-flex items-center justify-center font-bold">M</div>
                    <div>
                      <div className="text-sm font-semibold">Mick</div>
                      <div className="text-[11px] text-white/50 inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> Online · Free to chat</div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-3" data-testid="mick-chat-preview">
                    <div className="flex justify-end">
                      <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-[color:var(--kc-gold)] text-black px-4 py-2.5 text-sm">My head won’t shut off tonight.</div>
                    </div>
                    <div className="flex">
                      <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white/10 border border-white/10 px-4 py-2.5 text-sm leading-relaxed">
                        Yeah, that loop is rough. Try this: write down the loudest thought, then ask it — “Is this true, or is this fear talking?” Sometimes naming it is the first crack in the spiral.
                      </div>
                    </div>
                    <div className="flex">
                      <div className="max-w-[60%] rounded-2xl rounded-tl-sm bg-white/10 border border-white/10 px-4 py-2.5 text-sm inline-flex gap-1 items-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse" />
                        <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse [animation-delay:120ms]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse [animation-delay:240ms]" />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 h-10 text-white/40 text-sm">
                    Talk to Mick… <span className="ml-auto inline-flex items-center justify-center w-7 h-7 rounded-full bg-[color:var(--kc-gold)] text-black"><Send size={12} /></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ 4) FEATURED COURSES ============ */}
      {vis.featured_courses !== false && (
        <section className="bg-white py-16 md:py-24" data-testid="splash-courses">
          <div className="kc-container">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-8">
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-muted-kc">The Library</div>
                <h2 className="font-display uppercase mt-2 leading-[0.95]" style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}>Featured Courses</h2>
              </div>
              <Link to="/courses" className="text-sm font-semibold inline-flex items-center gap-1 hover:text-gold transition-colors" data-testid="see-all-courses">See all <ChevronRight size={16} /></Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {headspace && (
                <Link to="/courses/headspace" className="group relative rounded-[var(--kc-radius-md)] overflow-hidden border border-kc-strong hover:border-gold transition-all" data-testid="course-card-headspace">
                  <div className="aspect-[3/4] overflow-hidden bg-ink">
                    <img src={headspace.cover_image} onError={(e) => { if (e.currentTarget.src.indexOf('/splash/headspace_cover.png') === -1) e.currentTarget.src = '/splash/headspace_cover.png'; }} alt="HEADSPACE" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[color:var(--kc-gold)] text-black text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Available</div>
                  <div className="p-3">
                    <div className="font-display uppercase text-lg leading-tight">HEADSPACE</div>
                    <div className="text-xs text-muted-kc mt-0.5">{headspace.lesson_count} lessons</div>
                  </div>
                </Link>
              )}
              {comingSoon.map((cs) => (
                <div key={cs.slug} className="group relative rounded-[var(--kc-radius-md)] overflow-hidden border border-kc opacity-90 hover:opacity-100 transition-opacity" data-testid={`course-card-${cs.slug}`}>
                  <div className="aspect-[3/4] overflow-hidden bg-ink relative">
                    <img src={cs.image || '/splash/manifesto_storm.png'} alt="" className="w-full h-full object-cover opacity-35 group-hover:opacity-45 transition-opacity" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-3 text-center">
                      <Lock className="text-[color:var(--kc-gold)] mb-2" size={22} />
                      <div className="font-display uppercase text-white text-base md:text-lg leading-tight">{cs.title}</div>
                      <div className="text-[10px] text-white/70 tracking-[0.18em] uppercase mt-1">{cs.subtitle}</div>
                      <div className="mt-3">
                        <WaitlistBell type="course" courseSlug={cs.slug} courseTitle={cs.title} count={counts.courses?.[cs.slug] ?? 0} />
                      </div>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-muted-kc">Coming Soon</div>
                    <div className="font-display uppercase text-base leading-tight">{cs.title}</div>
                    <div className="text-[10px] text-muted-kc mt-0.5">
                      {(counts.courses?.[cs.slug] ?? 0) > 0
                        ? `${counts.courses[cs.slug]} ${counts.courses[cs.slug] === 1 ? 'person' : 'people'} waiting`
                        : 'Be the first on the list'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ 5) PAY WHAT YOU WANT ============ */}
      {vis.pay_what_you_want !== false && (
        <section className="relative bg-ink text-white py-20 md:py-28 overflow-hidden" data-testid="splash-pwyw">
          <div className="absolute inset-0 pointer-events-none kc-noise opacity-50" />
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(800px 500px at 50% 0%, rgba(212,164,55,0.22), transparent 60%)',
          }} />
          <div className="relative kc-container max-w-4xl text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">
              <Heart size={12} /> {c.pwyw_eyebrow}
            </div>
            <h2 className="mt-5 font-display uppercase leading-[0.88]" style={{ fontSize: 'clamp(2.75rem, 7vw, 6rem)' }} data-testid="pwyw-title">
              {c.pwyw_title}
            </h2>
            <p className="mt-6 text-xl md:text-2xl text-white/85 leading-relaxed max-w-2xl mx-auto">{c.pwyw_body}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-2" data-testid="pwyw-tiers">
              {(c.pwyw_suggested || []).map((amt) => (
                <Link key={amt} to={`${c.pwyw_cta_href || '/courses/headspace'}?amount=${amt}`} className="h-12 px-5 inline-flex items-center justify-center rounded-full border border-white/20 text-white/85 font-semibold text-sm hover:bg-[color:var(--kc-gold)] hover:text-black hover:border-[color:var(--kc-gold)] transition-colors">
                  {amt === 0 ? 'Free' : `$${amt}`}
                </Link>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap justify-center items-center gap-4">
              <Link to={c.pwyw_cta_href || '/courses/headspace'} className="group h-14 px-8 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold text-lg hover:bg-[color:var(--kc-gold-2)] transition-all hover:gap-3 shadow-[0_10px_50px_-10px_rgba(212,164,55,0.7)]" data-testid="pwyw-cta">
                {c.pwyw_cta_label} <ArrowRight size={22} />
              </Link>
            </div>
            <p className="mt-6 text-sm text-white/55 max-w-xl mx-auto italic">{c.pwyw_note}</p>
          </div>
        </section>
      )}

      {/* ============ 5.5) GAMES SECTION — admin toggleable ============ */}
      {vis.games_section !== false && (
        <section className="relative overflow-hidden py-20 md:py-28" style={{ background: 'linear-gradient(135deg, #0B0B0C 0%, #1a1408 55%, #0B0B0C 100%)' }} data-testid="splash-games">
          {/* Decorative animated grid */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.07]" style={{
            backgroundImage: 'linear-gradient(rgba(212,164,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,164,55,0.5) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}/>
          <div className="absolute inset-0 pointer-events-none kc-noise opacity-50"/>
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full pointer-events-none animate-pulse" style={{ background: 'radial-gradient(circle, rgba(212,164,55,0.18), transparent 70%)' }}/>
          <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full pointer-events-none animate-pulse" style={{ background: 'radial-gradient(circle, rgba(212,164,55,0.12), transparent 70%)', animationDelay: '1.5s' }}/>

          <div className="relative kc-container">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold" data-testid="splash-games-eyebrow">
                <Zap size={12} className="animate-pulse"/> {c.games_section_eyebrow || 'PLAY THE WORK'}
              </div>
              <h2 className="mt-5 font-display uppercase leading-[0.92] text-white" style={{ fontSize: 'clamp(2.5rem, 6.5vw, 5.5rem)' }} data-testid="splash-games-title">
                {c.games_section_title_top || 'TRAIN YOUR MIND'}<br/>
                <span className="text-[color:var(--kc-gold)]">{c.games_section_title_bottom || 'LIKE A GAME.'}</span>
              </h2>
              <p className="mt-5 text-lg text-white/75 leading-relaxed" data-testid="splash-games-body">
                {c.games_section_body || 'Five fast, dopamine-hitting mini-games built straight into the lessons. Defend your headspace. Reframe the lies. Reset your nervous system. Earn XP. Climb the leaderboard.'}
              </p>
            </div>

            {/* Game cards grid — playful, tilted, hover-lifted */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-testid="splash-games-grid">
              {[
                { key: 'thought_defense', icon: Zap,       label: 'Thought Defense', tagline: 'Swipe the lies', href: '/games/thought-defense', tilt: '-rotate-2' },
                { key: 'reframe_battle',  icon: RefreshCw, label: 'Reframe Battle',  tagline: 'Rewrite the script', href: '/games/reframe-battle', tilt: 'rotate-2' },
                { key: 'breath_rhythm',   icon: Wind,      label: 'Breath Rhythm',   tagline: '4-7-8 reset', href: '/games/breath-rhythm', tilt: '-rotate-1' },
                { key: 'streak_tree',     icon: TreePine,  label: 'Mindset Tree',    tagline: 'Grow your streak', href: '/games/streak-tree', tilt: 'rotate-1' },
                { key: 'inner_battle',    icon: Brain,     label: 'Inner Battle',    tagline: 'See your mind', href: '/games/inner-battle', tilt: '-rotate-2' },
              ].map((g) => {
                const Icon = g.icon;
                return (
                  <Link
                    key={g.key}
                    to={g.href}
                    className={`group relative ${g.tilt} hover:rotate-0 hover:-translate-y-2 transition-all duration-300`}
                    data-testid={`splash-games-card-${g.key}`}
                  >
                    <div className="relative p-5 rounded-[var(--kc-radius-md)] bg-white/[0.04] backdrop-blur-sm border border-white/10 hover:border-[color:var(--kc-gold)]/60 hover:bg-white/[0.07] transition-colors h-full flex flex-col">
                      <div className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-[color:var(--kc-gold)] text-black flex items-center justify-center shadow-[0_8px_20px_-4px_rgba(212,164,55,0.6)] group-hover:scale-110 transition-transform">
                        <Icon size={18}/>
                      </div>
                      <div className="text-white font-display uppercase text-lg leading-tight mt-3">{g.label}</div>
                      <div className="mt-1 text-xs text-white/55 leading-relaxed">{g.tagline}</div>
                      <div className="mt-auto pt-4 text-[10px] uppercase tracking-[0.18em] font-semibold text-[color:var(--kc-gold)] inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        Play <ArrowRight size={11}/>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* CTA row */}
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to={c.games_section_cta_href || '/games'}
                className="group h-13 px-7 py-3 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold text-base hover:bg-[color:var(--kc-gold-2)] transition-all hover:gap-3 shadow-[0_15px_40px_-10px_rgba(212,164,55,0.55)]"
                data-testid="splash-games-cta"
              >
                {c.games_section_cta_label || 'Enter the arena'} <ArrowRight size={16}/>
              </Link>
              <Link
                to="/games/leaderboard"
                className="h-13 px-7 py-3 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors"
                data-testid="splash-games-lb"
              >
                <Trophy size={16} className="text-[color:var(--kc-gold)]"/> See the leaderboard
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ============ 5.6) HEADSPACE — THE SONG ============ */}
      {vis.song_section !== false && c.song_audio_url && (
        <SongSection c={c} headspaceCover={headspaceCover} />
      )}

      {/* ============ 6) MICKEY'S STORY ============ */}
      {vis.my_story !== false && (
        <section className="relative bg-white py-16 md:py-24 overflow-hidden" data-testid="splash-mystory">
          <div className="kc-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="relative rounded-[var(--kc-radius-md)] overflow-hidden border border-kc-strong shadow-2xl">
                <img src="/splash/mickey_portrait.png" alt="Mickey Trivett" className="w-full block aspect-[4/5] object-cover" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-white">
                  <div className="font-display uppercase text-xl">Mickey Trivett</div>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-white/70">Author · Mindset Coach</div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]">My Story</div>
              <h2 className="mt-3 font-display uppercase leading-[0.92]" style={{ fontSize: 'clamp(2.25rem, 5.5vw, 4.5rem)' }}>
                I’ve been there.<br />I built a way <span className="text-[color:var(--kc-gold)]">out.</span>
              </h2>
              <p className="mt-5 text-lg text-ink-2 leading-relaxed max-w-xl">
                At a year and a half old, a steel garden rake went through my skull. Doctors told my parents I might never speak normally. At thirteen, I lost my older brother Tony. In my forties, anxiety found me in ways I never expected. I’ve battled overthinking, doubt, and the voice in my head that tries to write a story about my life that isn’t true.
              </p>
              <p className="mt-4 text-lg text-ink-2 leading-relaxed max-w-xl">
                I turned that pain into purpose. Now I teach what I had to learn the hard way. Not perfect. Not fearless. Useful.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link to="/about" className="group h-13 px-7 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-all hover:gap-3 shadow-[0_10px_30px_-10px_rgba(212,164,55,0.45)]" data-testid="story-about-cta">Read my full story <ArrowRight size={16}/></Link>
                <Link to="/about#about-bookme" className="h-13 px-7 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] border border-ink/30 hover:bg-ink hover:text-white transition-colors font-semibold text-ink" data-testid="story-book-cta">Book Mickey</Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ 7) ANIMATED STATS ============ */}
      {vis.stats !== false && (
        <section className="relative bg-ink text-white py-20 md:py-28 overflow-hidden" data-testid="splash-stats">
          <div className="absolute inset-0 pointer-events-none kc-noise opacity-50" />
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(700px 380px at 50% 50%, rgba(212,164,55,0.12), transparent 60%)',
          }} />
          {/* Slow background pan */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{
            backgroundImage: 'radial-gradient(circle at 25% 50%, rgba(255,255,255,0.4), transparent 30%), radial-gradient(circle at 75% 50%, rgba(212,164,55,0.4), transparent 30%)',
            animation: 'kcStatsPan 18s ease-in-out infinite alternate',
          }} />
          <div className="relative kc-container">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]">{c.stats_eyebrow}</div>
              <h2 className="mt-3 font-display uppercase leading-[0.92]" style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}>By the numbers</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-5xl mx-auto">
              {(c.stats_items || []).map((s, i) => (
                <AnimatedStat key={i} value={s.value} suffix={s.suffix} label={s.label} delay={i * 150} />
              ))}
            </div>
          </div>
          <style>{`@keyframes kcStatsPan { from { transform: translate3d(-3%, -2%, 0); } to { transform: translate3d(3%, 2%, 0); } }`}</style>
        </section>
      )}

      {/* ============ 8) RESOURCES & TOOLS ============ */}
      {vis.resources !== false && (
        <section className="relative bg-white py-16 md:py-24" data-testid="splash-resources">
          <div className="kc-container">
            <div className="max-w-2xl mb-10">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]">{c.resources_eyebrow}</div>
              <h2 className="mt-3 font-display uppercase leading-[0.92]" style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}>{c.resources_title}</h2>
              <p className="mt-4 text-lg text-ink-2 leading-relaxed">{c.resources_body}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: FileText, title: 'Journal Guide', body: 'The full no-fluff playbook for journaling that actually moves the needle.', href: '/journal-guide', cta: 'Start the guide' },
                { icon: BookOpen, title: 'Mickey’s Bookshelf', body: 'The books that shaped my mindset — with notes on why they matter.', href: '/bookshelf', cta: 'See bookshelf' },
                { icon: ShieldCheck, title: 'Journal Accountability', body: 'No content stored. Just a daily check-in to keep your streak alive.', href: '/resources', cta: 'Start your streak' },
                { icon: MessageCircle, title: 'Daily Prompts', body: 'A new prompt every day to clear, reset, and refocus.', href: '/community', cta: 'See today’s prompt' },
                { icon: Library, title: 'Vision Board Builder', body: 'Build a board that actually means something. Drag, drop, export.', href: '/vision-board', cta: 'Build yours' },
                { icon: Heart, title: 'Crisis Support', body: 'You don’t have to carry it alone. Free, confidential, 24/7.', href: '#crisis', cta: 'Get help now' },
              ].map((r, i) => {
                const Icon = r.icon;
                const inner = (
                  <>
                    <div className="w-12 h-12 rounded-full bg-gold-soft text-[color:var(--kc-gold-2)] inline-flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-transform"><Icon size={20} /></div>
                    <h3 className="font-display uppercase text-2xl leading-tight">{r.title}</h3>
                    <p className="mt-2 text-sm text-ink-2 leading-relaxed">{r.body}</p>
                    <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-ink group-hover:text-[color:var(--kc-gold-2)] transition-colors">{r.cta} <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" /></div>
                  </>
                );
                const cls = "group relative block rounded-[var(--kc-radius-md)] border border-kc hover:border-[color:var(--kc-gold)] bg-white p-6 transition-all hover:shadow-[0_20px_50px_-20px_rgba(212,164,55,0.4)] hover:-translate-y-0.5";
                if (r.href.startsWith('#')) {
                  return <a key={i} href={r.href} className={cls} data-testid={`resource-card-${i}`}>{inner}</a>;
                }
                return <Link key={i} to={r.href} className={cls} data-testid={`resource-card-${i}`}>{inner}</Link>;
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============ 9) COMMUNITY PREVIEW ============ */}
      {vis.community !== false && (
        <section className="relative bg-ink text-white py-16 md:py-24 overflow-hidden" data-testid="splash-community">
          <div className="absolute inset-0 pointer-events-none kc-noise opacity-50" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(700px 400px at 20% 50%, rgba(212,164,55,0.12), transparent 60%)' }} />
          <div className="relative kc-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">
                <Users size={12} /> {c.community_eyebrow}
              </div>
              <h2 className="mt-4 font-display uppercase leading-[0.9]" style={{ fontSize: 'clamp(2.25rem, 6vw, 5rem)' }} data-testid="community-title">{c.community_title}</h2>
              <p className="mt-5 text-lg text-white/80 max-w-xl leading-relaxed">{c.community_body}</p>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <Link to="/community" className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="splash-community-cta"><Users size={16}/> Enter the community</Link>
                <WaitlistBell type="community" variant="full" count={counts.community ?? 0} />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative bg-white/5 backdrop-blur-md border border-white/10 rounded-[var(--kc-radius-md)] p-5">
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/50 mb-3">What you’ll find inside</div>
                <ul className="space-y-2.5 text-sm">
                  {['Member profiles', 'Mindset challenges', 'Accountability check-ins', 'Wins & community stories', 'Live sessions with Mick', 'Private + public groups'].map((x, i) => (
                    <li key={i} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[color:var(--kc-gold)] flex-shrink-0" /> {x}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ 10) TESTIMONIALS ============ */}
      {vis.testimonials !== false && (
        <section className="bg-white py-16 md:py-24" data-testid="splash-testimonials">
          <div className="kc-container">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]">Real Talk</div>
              <h2 className="mt-3 font-display uppercase leading-[0.92]" style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}>What people are saying</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(c.testimonials || []).map((t, i) => (
                <div key={i} className="relative bg-white border border-kc rounded-[var(--kc-radius-md)] p-6 hover:border-[color:var(--kc-gold)] transition-colors" data-testid={`testimonial-${i}`}>
                  <Quote className="text-[color:var(--kc-gold)]" size={22} />
                  <p className="mt-3 text-base text-ink-2 leading-relaxed italic">“{t.quote}”</p>
                  <div className="mt-5 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-ink text-white inline-flex items-center justify-center font-bold">{(t.name || '?')[0]}</div>
                    <div>
                      <div className="font-semibold text-sm">{t.name}</div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-muted-kc">{t.label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ 11) CRISIS SUPPORT ============ */}
      {vis.crisis !== false && (
        <section id="crisis" className="relative bg-ink text-white py-16 md:py-20" data-testid="splash-crisis">
          <div className="kc-container">
            <div className="relative max-w-4xl mx-auto rounded-[var(--kc-radius-md)] border-2 border-[color:var(--kc-gold)] bg-gradient-to-br from-ink to-black p-6 md:p-10 overflow-hidden">
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(500px 280px at 90% 10%, rgba(212,164,55,0.18), transparent 60%)' }} />
              <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                <div className="md:col-span-8">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[color:var(--kc-gold)] font-bold">{c.crisis_eyebrow}</div>
                  <h2 className="mt-3 font-display uppercase text-3xl md:text-4xl leading-tight">{c.crisis_title}</h2>
                  <p className="mt-3 text-white/80 leading-relaxed">{c.crisis_body}</p>
                </div>
                <div className="md:col-span-4 flex flex-col gap-2.5">
                  <a href={`tel:${c.crisis_phone || '988'}`} className="group h-13 px-5 py-3 inline-flex items-center justify-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="crisis-call-btn">
                    <Phone size={18} className="kc-phone-ring" /> Call {c.crisis_phone || '988'}
                  </a>
                  <a href={`sms:${c.crisis_text_target || '988'}`} className="h-13 px-5 py-3 inline-flex items-center justify-center gap-2 rounded-[var(--kc-radius-sm)] border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors" data-testid="crisis-text-btn">
                    <MessageSquare size={18} /> Text {c.crisis_text_target || '988'}
                  </a>
                  <a href={c.crisis_url || 'https://988lifeline.org'} target="_blank" rel="noopener noreferrer" className="text-center text-xs uppercase tracking-[0.18em] text-white/60 hover:text-[color:var(--kc-gold)] transition-colors mt-1">Visit 988lifeline.org →</a>
                </div>
              </div>
            </div>
          </div>
          <style>{`
            @keyframes kcPhoneRing { 0%, 100% { transform: rotate(0); } 10% { transform: rotate(-14deg); } 20% { transform: rotate(14deg); } 30% { transform: rotate(-10deg); } 40% { transform: rotate(10deg); } 50% { transform: rotate(0); } }
            .kc-phone-ring { animation: kcPhoneRing 2.2s ease-in-out infinite; transform-origin: 50% 50%; }
          `}</style>
        </section>
      )}

      {/* ============ FINAL CTA ============ */}
      <section className="relative bg-white py-16 md:py-20" data-testid="splash-cta">
        <div className="kc-container text-center max-w-3xl mx-auto">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]">{c.cta_eyebrow}</div>
          <h2 className="mt-3 font-display uppercase leading-[0.88]" style={{ fontSize: 'clamp(2.25rem, 6vw, 5rem)' }}>
            {c.cta_title_top}<br /><span className="text-[color:var(--kc-gold)]">{c.cta_title_bottom}</span>
          </h2>
          <p className="mt-5 text-lg md:text-xl text-ink-2 max-w-xl mx-auto">{c.cta_body}</p>
          <div className="mt-7">
            <Link to={c.cta_button_href || '/courses/headspace'} className="group h-14 px-8 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold text-lg hover:bg-[color:var(--kc-gold-2)] transition-all hover:gap-3 shadow-[0_10px_40px_-10px_rgba(212,164,55,0.6)]" data-testid="splash-cta-button">
              {c.cta_button_label} <ArrowRight size={22} />
            </Link>
          </div>
          <div className="mt-8 font-display tracking-[0.28em] uppercase text-muted-kc text-sm md:text-base">
            LIVE LIFE. <span className="text-[color:var(--kc-gold)]">DO GOOD.</span> BE HAPPY.
          </div>
        </div>
      </section>
    </div>
  );
}

/* ============================================================
 * HEADSPACE — THE SONG SECTION
 * Cinematic black soundtrack moment with spinning vinyl + waveform.
 * ============================================================ */
function SongSection({ c, headspaceCover }) {
  const audioRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(false);
  const [progress, setProgress] = React.useState(0); // 0..1
  const [duration, setDuration] = React.useState(0);
  const [current, setCurrent] = React.useState(0);
  const [showLyrics, setShowLyrics] = React.useState(false);

  const artwork = (c.song_artwork_url && c.song_artwork_url.trim()) || headspaceCover;
  const lyrics = (c.song_lyrics || '').trim();

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  React.useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => {
      setCurrent(el.currentTime || 0);
      if (el.duration && !Number.isNaN(el.duration)) {
        setProgress((el.currentTime || 0) / el.duration);
      }
    };
    const onLoaded = () => { if (el.duration && !Number.isNaN(el.duration)) setDuration(el.duration); };
    const onEnd = () => { setPlaying(false); setProgress(0); setCurrent(0); };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onLoaded);
    el.addEventListener('ended', onEnd);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onLoaded);
      el.removeEventListener('ended', onEnd);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
    };
  }, []);

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.currentTime = pct * el.duration;
    setProgress(pct);
  };

  const fmt = (sec) => {
    if (!sec || Number.isNaN(sec)) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Waveform bars — animated when playing
  const bars = React.useMemo(() => Array.from({ length: 48 }, (_, i) => ({
    h: 25 + Math.sin(i * 0.7) * 18 + (i % 3) * 10 + Math.random() * 16,
    d: (i * 0.05) % 0.9,
  })), []);

  return (
    <section
      className="relative overflow-hidden py-20 md:py-28"
      style={{ background: 'linear-gradient(180deg, #050505 0%, #0B0B0C 50%, #050505 100%)' }}
      data-testid="splash-song"
    >
      {/* Decorative grid + glow */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{
        backgroundImage: 'linear-gradient(rgba(212,164,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,164,55,0.5) 1px, transparent 1px)',
        backgroundSize: '48px 48px',
      }}/>
      <div className="absolute inset-0 pointer-events-none kc-noise opacity-50"/>
      <div className={`absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full pointer-events-none transition-opacity duration-700 ${playing ? 'opacity-100' : 'opacity-50'}`} style={{
        background: 'radial-gradient(circle, rgba(212,164,55,0.18), transparent 70%)',
      }}/>

      <div className="relative kc-container">
        {/* Eyebrow */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold" data-testid="song-eyebrow">
            <Music size={12}/> {c.song_eyebrow || 'THE SOUNDTRACK BEHIND THE MESSAGE'}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* === Spinning vinyl === */}
          <div className="lg:col-span-5 flex justify-center">
            <button
              type="button"
              onClick={toggle}
              aria-label={playing ? 'Pause song' : 'Play song'}
              className="group relative w-64 h-64 md:w-80 md:h-80 rounded-full focus:outline-none focus-visible:ring-4 focus-visible:ring-[color:var(--kc-gold)]/40"
              data-testid="song-vinyl"
            >
              {/* Outer vinyl disc */}
              <div className={`absolute inset-0 rounded-full ${playing ? 'kc-vinyl-spin' : ''} transition-transform duration-700`} style={{
                background: 'radial-gradient(circle at 50% 50%, #1a1a1a 0%, #0a0a0a 60%, #000 100%)',
                boxShadow: '0 30px 80px -20px rgba(212,164,55,0.35), inset 0 0 0 1px rgba(212,164,55,0.18)',
              }}>
                {/* Grooves */}
                {[0.86, 0.78, 0.70, 0.62, 0.54].map((s, i) => (
                  <div key={i} className="absolute rounded-full border border-white/[0.04]" style={{
                    top: `${(1 - s) * 50}%`, left: `${(1 - s) * 50}%`, width: `${s * 100}%`, height: `${s * 100}%`,
                  }}/>
                ))}
                {/* Subtle reflection */}
                <div className="absolute inset-0 rounded-full pointer-events-none" style={{
                  background: 'conic-gradient(from 200deg at 50% 50%, transparent 0deg, rgba(255,255,255,0.04) 40deg, transparent 80deg, transparent 200deg, rgba(212,164,55,0.05) 240deg, transparent 280deg)',
                }}/>
                {/* Center label with artwork */}
                <div className="absolute inset-[18%] rounded-full overflow-hidden border-2 border-[color:var(--kc-gold)]/30 bg-ink">
                  <img src={artwork} alt="HEADSPACE" className="w-full h-full object-cover" onError={(e) => { if (e.currentTarget.src.indexOf('/splash/headspace_cover.png') === -1) e.currentTarget.src = '/splash/headspace_cover.png'; }}/>
                  <div className="absolute inset-0 bg-black/30 mix-blend-multiply"/>
                </div>
                {/* Spindle hole */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-black border border-[color:var(--kc-gold)]/40"/>
              </div>

              {/* Play / pause overlay */}
              <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${playing ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[color:var(--kc-gold)] text-black inline-flex items-center justify-center shadow-[0_15px_40px_-8px_rgba(212,164,55,0.7)] group-hover:scale-110 transition-transform">
                  {playing ? <Pause size={28} fill="currentColor"/> : <Play size={28} fill="currentColor" className="ml-1"/>}
                </div>
              </div>
            </button>
          </div>

          {/* === Text + waveform + controls === */}
          <div className="lg:col-span-7">
            <h2 className="font-display uppercase leading-[0.88] text-white" style={{ fontSize: 'clamp(2.75rem, 7vw, 6rem)' }} data-testid="song-title">
              {c.song_title || 'HEADSPACE'}
            </h2>
            <div className="mt-1 font-display uppercase text-[color:var(--kc-gold)] leading-tight" style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)' }} data-testid="song-subtitle">
              {c.song_subtitle || 'The Song'}
            </div>
            <p className="mt-6 text-lg md:text-xl text-white/75 max-w-xl leading-relaxed italic" data-testid="song-body">
              “{c.song_body || 'A song about the battles people fight silently every day.'}”
            </p>

            {/* Waveform */}
            <div className="mt-7 flex items-end gap-[3px] h-14 md:h-16 max-w-md" aria-hidden="true">
              {bars.map((b, i) => (
                <span
                  key={i}
                  className={`flex-1 rounded-sm transition-colors ${playing ? 'kc-wave-bar' : ''}`}
                  style={{
                    height: `${b.h}%`,
                    background: i / bars.length < progress
                      ? 'linear-gradient(180deg, #E5BE5C, #D4A437)'
                      : 'rgba(255,255,255,0.18)',
                    animationDelay: `${b.d}s`,
                  }}
                />
              ))}
            </div>

            {/* Scrubber */}
            <div
              onClick={seek}
              role="slider"
              aria-label="Song progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress * 100)}
              tabIndex={0}
              className="mt-3 h-1.5 w-full max-w-md rounded-full bg-white/10 cursor-pointer relative overflow-hidden hover:bg-white/15 transition-colors"
              data-testid="song-scrubber"
            >
              <div className="absolute inset-y-0 left-0 bg-[color:var(--kc-gold)] rounded-full transition-[width] duration-150" style={{ width: `${progress * 100}%` }}/>
            </div>
            <div className="mt-1.5 flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-white/55 font-semibold tabular-nums max-w-md">
              <span data-testid="song-time-current">{fmt(current)}</span>
              <span className="flex-1 h-px bg-white/10"/>
              <span data-testid="song-time-total">{duration ? fmt(duration) : '—'}</span>
            </div>

            {/* CTA + credit */}
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={toggle}
                className="group h-13 px-7 py-3 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold text-base hover:bg-[color:var(--kc-gold-2)] transition-all hover:gap-3 shadow-[0_15px_40px_-10px_rgba(212,164,55,0.55)]"
                data-testid="song-listen-cta"
              >
                {playing ? <><Pause size={18} fill="currentColor"/> Pause</> : <><Play size={18} fill="currentColor"/> {c.song_button_label || 'LISTEN NOW'}</>}
              </button>
              {c.song_credit_line && (
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/50 font-semibold" data-testid="song-credit">
                  <Disc3 size={12} className="text-[color:var(--kc-gold)]"/> {c.song_credit_line}
                </div>
              )}
            </div>

            {/* Lyrics expandable */}
            {lyrics && (
              <div className="mt-8 max-w-2xl">
                <button
                  type="button"
                  onClick={() => setShowLyrics((v) => !v)}
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-[color:var(--kc-gold)] hover:text-[color:var(--kc-gold-2)] transition-colors"
                  data-testid="song-lyrics-toggle"
                >
                  <ChevronDown size={14} className={`transition-transform ${showLyrics ? 'rotate-180' : ''}`}/> {showLyrics ? 'Hide lyrics' : 'Read the lyrics'}
                </button>
                {showLyrics && (
                  <pre
                    className="mt-3 p-5 rounded-[var(--kc-radius-md)] bg-white/[0.03] border border-white/10 text-white/80 leading-relaxed whitespace-pre-wrap font-reader text-base"
                    data-testid="song-lyrics"
                  >
{lyrics}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Hidden audio element */}
        <audio ref={audioRef} preload="metadata" src={c.song_audio_url} data-testid="song-audio"/>
      </div>

      <style>{`
        @keyframes kcVinylSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .kc-vinyl-spin { animation: kcVinylSpin 4.5s linear infinite; }
        @keyframes kcWaveBar { 0%,100% { transform: scaleY(0.85); } 50% { transform: scaleY(1.15); } }
        .kc-wave-bar { animation: kcWaveBar 0.95s ease-in-out infinite; transform-origin: bottom; }
      `}</style>
    </section>
  );
}
